/**
 * Teams Service
 * API calls for teams management
 */

import apiClient from '../lib/axios';

export interface TeamRoleMember {
  member_id: number;
  first_name: string;
  last_name: string;
  email?: string;
  has_profile_picture?: boolean;
}

export interface Team {
  id: number;
  name: string;
  department_id: number | null;
  department_name?: string | null;
  created_at: string;
  description?: string | null;
  team_logo?: string | null;
  team_logo_url?: string | null;
  has_team_logo?: boolean;
  is_active?: number;
  member_count?: number;
  managers?: TeamRoleMember[];
  leads?: TeamRoleMember[];
}

export interface TeamFormData {
  name: string;
  manager_ids?: number[];
  lead_ids?: number[];
  department_id?: number | null;
  team_logo?: File;
  date_created?: string;
  description?: string | null;
  is_active?: number | boolean;
}

// Pagination interfaces
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  meta: PaginationMeta;
}

export interface TeamsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
  department_id?: number;
}

// Image cache for blob URLs
const imageCache = new Map<string, string>();

export const teamsService = {
  /**
   * Get paginated teams with filters
   */
  getPaginated: async (params: TeamsQueryParams = {}): Promise<PaginatedResponse<Team>> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.search) queryParams.set('search', params.search);
    if (params.sort) queryParams.set('sort', params.sort);
    if (params.department_id) queryParams.set('department_id', String(params.department_id));

    const queryString = queryParams.toString();
    const url = queryString ? `/teams?${queryString}` : '/teams';

    const { data } = await apiClient.get<PaginatedResponse<Team>>(url);
    return data;
  },

  /**
   * Get all teams
   * @deprecated Use getPaginated instead
   */
  getAll: async (): Promise<Team[]> => {
    // Fetch all teams by using a high limit
    const { data } = await apiClient.get<PaginatedResponse<Team>>('/teams?limit=100&page=1');
    // Handle both old array response and new paginated response
    if (Array.isArray(data)) {
      return data;
    }
    if (data && Array.isArray(data.data)) {
      return data.data;
    }
    console.error('API returned unexpected data format for teams:', data);
    return [];
  },

  /**
   * Get team by ID
   */
  getById: async (id: number): Promise<Team> => {
    const { data } = await apiClient.get<Team>(`/team/${id}`);
    return data;
  },

  /**
   * Check if team name already exists
   */
  checkNameExists: async (name: string, excludeTeamId?: number): Promise<{ exists: boolean }> => {
    const params = new URLSearchParams({
      action: 'check_name',
      name: name.trim(),
    });
    if (excludeTeamId) {
      params.append('exclude_id', String(excludeTeamId));
    }
    const { data } = await apiClient.get<{ exists: boolean }>(`/teams?${params.toString()}`);
    return data;
  },

  /**
   * Create new team
   */
  create: async (teamData: TeamFormData): Promise<Team> => {
    // Generate current date in YYYY-MM-DD format if not provided
    const date_created = teamData.date_created || new Date().toISOString().split('T')[0];

    // If there's a logo, use FormData; otherwise JSON
    if (teamData.team_logo) {
      const formData = new FormData();
      formData.append('name', teamData.name);
      formData.append('date_created', date_created);
      if (teamData.department_id) {
        formData.append('department_id', String(teamData.department_id));
      }
      if (teamData.manager_ids && teamData.manager_ids.length > 0) {
        formData.append('manager_ids', JSON.stringify(teamData.manager_ids));
      }
      if (teamData.lead_ids && teamData.lead_ids.length > 0) {
        formData.append('lead_ids', JSON.stringify(teamData.lead_ids));
      }
      formData.append('team_logo', teamData.team_logo);

      const { data } = await apiClient.post<Team>('/teams', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (teamData.team_logo && !data.has_team_logo) {
        console.warn('Team created but logo upload may have failed. Check server logs.');
      }

      return data;
    } else {
      const payload = {
        ...teamData,
        date_created,
      };
      const { data } = await apiClient.post<Team>('/teams', payload);
      return data;
    }
  },

  /**
   * Update team (uses POST with multipart for file uploads)
   */
  update: async (id: number, teamData: TeamFormData): Promise<Team> => {
    // If there's a logo, use FormData with POST; otherwise use PUT with JSON
    if (teamData.team_logo) {
      const formData = new FormData();
      formData.append('name', teamData.name);
      if (teamData.date_created) {
        formData.append('date_created', teamData.date_created);
      }
      if (teamData.department_id !== undefined) {
        formData.append('department_id', teamData.department_id !== null ? String(teamData.department_id) : '');
      }
      if (teamData.manager_ids !== undefined) {
        formData.append('manager_ids', JSON.stringify(teamData.manager_ids));
      }
      if (teamData.lead_ids !== undefined) {
        formData.append('lead_ids', JSON.stringify(teamData.lead_ids));
      }
      formData.append('team_logo', teamData.team_logo);

      const { data } = await apiClient.post<Team>(`/team/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (teamData.team_logo && !data.has_team_logo) {
        console.warn('Team updated but logo upload may have failed. Check server logs.');
      }

      return data;
    } else {
      const { data } = await apiClient.put<Team>(`/team/${id}`, teamData);
      return data;
    }
  },

  /**
   * Add a single team manager (sub-route).
   */
  addManager: async (teamId: number, memberId: number): Promise<{ success: boolean; id: number }> => {
    const { data } = await apiClient.post<{ success: boolean; id: number }>(
      `/team/${teamId}/managers`,
      { member_id: memberId }
    );
    return data;
  },

  /**
   * Remove a single team manager (sub-route).
   */
  removeManager: async (teamId: number, memberId: number): Promise<void> => {
    await apiClient.delete(`/team/${teamId}/managers/${memberId}`);
  },

  /**
   * Add a single team lead (sub-route).
   */
  addLead: async (teamId: number, memberId: number): Promise<{ success: boolean; id: number }> => {
    const { data } = await apiClient.post<{ success: boolean; id: number }>(
      `/team/${teamId}/leads`,
      { member_id: memberId }
    );
    return data;
  },

  /**
   * Remove a single team lead (sub-route).
   */
  removeLead: async (teamId: number, memberId: number): Promise<void> => {
    await apiClient.delete(`/team/${teamId}/leads/${memberId}`);
  },

  /**
   * Delete team
   */
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/team/${id}`);
  },

  /**
   * Fetch team logo as blob URL (with JWT authentication)
   * Images are fetched via axios which includes JWT token in Authorization header
   */
  fetchTeamImage: async (teamId: number): Promise<string | null> => {
    try {
      // Check cache first
      const cacheKey = `team_${teamId}`;
      if (imageCache.has(cacheKey)) {
        return imageCache.get(cacheKey)!;
      }

      // Fetch image as blob with JWT authentication
      const response = await apiClient.get<Blob>(`/team_image/${teamId}`, {
        responseType: 'blob',
        params: { t: Date.now() }, // Cache busting
      });

      // Create blob URL
      const blobUrl = URL.createObjectURL(response.data);

      // Cache the blob URL
      imageCache.set(cacheKey, blobUrl);

      return blobUrl;
    } catch (error) {
      console.error(`Failed to load image for team ${teamId}:`, error);
      return null;
    }
  },

  /**
   * Revoke blob URL and remove from cache
   */
  revokeTeamImage: (teamId: number): void => {
    const cacheKey = `team_${teamId}`;
    const blobUrl = imageCache.get(cacheKey);

    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      imageCache.delete(cacheKey);
    }
  },

  /**
   * Clear all cached team images
   */
  clearTeamImageCache: (): void => {
    imageCache.forEach((blobUrl) => {
      URL.revokeObjectURL(blobUrl);
    });
    imageCache.clear();
  },
};
